import { Hand, Bone, Activity, Users, Waves, Zap } from "lucide-react";

const KINESIOLOGY_IMG = "/kinesiology.jpg";
const EXTREMITY_IMG = "/extremity.jpg";
const ALL_AGES_IMG = "/young-patient.jpg";

export default function Services() {
  return (
    <section id="services" className="py-24 lg:py-32 bg-[#F7F5F0]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">
          What I Do
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A221E] tracking-tight leading-none mb-16">
          Care That Goes Beyond<br className="hidden sm:block" /> the Spine
        </h2>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Applied Kinesiology - tall card */}
          <div
            className="row-span-2 rounded-3xl overflow-hidden border border-[#E4DCCF] bg-white group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
            data-testid="service-kinesiology"
          >
            <div className="grid grid-cols-2 h-72">
              <div className="overflow-hidden">
                <img
                  src="/ak-arm.jpg"
                  alt="Dr. Greenspan performing applied kinesiology arm assessment"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="overflow-hidden">
                <img
                  src="/ak-leg.jpg"
                  alt="Dr. Greenspan performing applied kinesiology leg assessment"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="p-8 lg:p-10">
              <div className="w-10 h-10 rounded-full bg-[#C87B5D]/10 flex items-center justify-center mb-4">
                <Hand size={18} className="text-[#C87B5D]" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#3A4F41] tracking-tight mb-4">
                Applied Kinesiology
              </h3>
              <p className="font-body text-base text-[#5C6A61] leading-relaxed">
                A diagnostic system that uses manual muscle testing to evaluate body function. By identifying imbalances in your structural, chemical, and emotional health, we develop a personalized treatment plan.
              </p>
            </div>
          </div>

          {/* Extremity Adjustments - athlete elbow photo */}
          <div
            className="rounded-3xl overflow-hidden border border-[#E4DCCF] bg-white group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
            data-testid="service-extremity"
          >
            <div className="flex flex-col sm:flex-row">
              <div className="sm:w-2/5 h-48 sm:h-auto overflow-hidden">
                <img
                  src={EXTREMITY_IMG}
                  alt="Athlete extremity adjustment"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="sm:w-3/5 p-8">
                <div className="w-10 h-10 rounded-full bg-[#3A4F41]/10 flex items-center justify-center mb-4">
                  <Bone size={18} className="text-[#3A4F41]" />
                </div>
                <h3 className="font-heading text-xl sm:text-2xl font-medium text-[#3A4F41] tracking-tight mb-3">
                  Extremity Adjustments
                </h3>
                <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                  Not just the spine — I work on shoulders, elbows, wrists, hips, knees, and ankles. I was trained by Dr. Kevin Herron, DC, CCSP at Life College of Chiropractic, a pioneer in extremity adjusting.
                </p>
              </div>
            </div>
          </div>

          {/* Full Spine */}
          <div
            className="rounded-3xl overflow-hidden border border-[#E4DCCF] bg-[#E4DCCF] group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
            data-testid="service-spine"
          >
            <div className="h-64 overflow-hidden">
              <img
                src="/spine-adjustment.png"
                alt="Chiropractic adjustment on prone patient"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8">
              <div className="w-10 h-10 rounded-full bg-[#3A4F41]/10 flex items-center justify-center mb-4">
                <Activity size={18} className="text-[#3A4F41]" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-medium text-[#1A221E] tracking-tight mb-3">
                Full Spine Chiropractic Care
              </h3>
              <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
                I utilize a variety of adjusting techniques — you pick what works best for you. For those wanting very light force, I offer Activator and ArthroStim adjustments. Prefer a more hands-on approach? I also use Thompson Drop Table techniques and traditional manual adjustments.
              </p>
            </div>
          </div>
        </div>

        {/* Trigenix + K-Laser */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-[#E4DCCF] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid="service-trigenix">
            <div className="w-10 h-10 rounded-full bg-[#C87B5D]/10 flex items-center justify-center mb-4">
              <Waves size={18} className="text-[#C87B5D]" />
            </div>
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-[#3A4F41] tracking-tight mb-3">Trigenix</h3>
            <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
              A 15-minute soft-tissue and muscle-reset technique used alongside adjusting, and bookable on its own. Dr. Greenspan will tell you at your visit whether it's appropriate for you.
            </p>
          </div>
          <div className="rounded-3xl border border-[#E4DCCF] bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid="service-klaser">
            <div className="w-10 h-10 rounded-full bg-[#3A4F41]/10 flex items-center justify-center mb-4">
              <Zap size={18} className="text-[#3A4F41]" />
            </div>
            <h3 className="font-heading text-xl sm:text-2xl font-medium text-[#3A4F41] tracking-tight mb-3">Class IV K-Laser Therapy</h3>
            <p className="font-body text-sm text-[#5C6A61] leading-relaxed">
              A 10-minute laser session applied to joints and soft tissue, bookable on its own or added to a visit. Dr. Greenspan will tell you at your visit whether it's appropriate for you.
            </p>
          </div>
        </div>

        {/* All Ages card - full width */}
        <div
          className="mt-6 rounded-3xl overflow-hidden border border-[#E4DCCF] bg-white group transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
          data-testid="service-all-ages"
        >
          <div className="flex flex-col md:flex-row">
            <div className="md:w-2/5 h-64 md:h-auto overflow-hidden">
              <img
                src={ALL_AGES_IMG}
                alt="Dr. Greenspan treats patients of all ages"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="md:w-3/5 p-8 lg:p-10 flex flex-col justify-center">
              <div className="w-10 h-10 rounded-full bg-[#C87B5D]/10 flex items-center justify-center mb-4">
                <Users size={18} className="text-[#C87B5D]" />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#3A4F41] tracking-tight mb-4">
                Patients of All Ages
              </h3>
              <p className="font-body text-base text-[#5C6A61] leading-relaxed">
                From babies to seniors, Dr. Greenspan works with patients at every stage of life. Chiropractic care isn't just for adults with back pain — children, athletes, and older adults are all seen here. Techniques are adapted to age and comfort.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
